import React, { useState, useEffect, useCallback } from 'react';

export default function YouTubeGallery() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [slidesPerView, setSlidesPerView] = useState(4);

  // Use environment variables (React requires REACT_APP_ prefix usually)
  const API_KEY = process.env.REACT_APP_YOUTUBE_API_KEY || 'AIzaSyA3RFb8giZuU6MOIrea24fi885pXUUed4M';
  const CHANNEL_ID = process.env.REACT_APP_YOUTUBE_CHANNEL_ID || 'UCd0gDC_DeUAuNQ4EfOrckPQ';
  const MAX_RESULTS = 12;

  const fetchYouTubeVideos = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      // LOW COST METHOD: 
      // 1. Convert Channel ID (UC...) to Uploads Playlist ID (UU...)
      const uploadsPlaylistId = CHANNEL_ID.replace(/^UC/, 'UU');

      // 2. Use playlistItems endpoint (Cost: 1 Unit) instead of search (Cost: 100 Units)
      const url = `https://www.googleapis.com/youtube/v3/playlistItems?part=snippet,contentDetails&maxResults=${MAX_RESULTS}&playlistId=${uploadsPlaylistId}&key=${API_KEY}`;
      
      const response = await fetch(url);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || 'Failed to fetch videos');
      }

      if (data.items && data.items.length > 0) {
        const videoData = data.items.map(item => ({
          id: item.contentDetails.videoId,
          title: item.snippet.title,
          subtitle: new Date(item.snippet.publishedAt).toLocaleDateString(),
          thumbnail: item.snippet.thumbnails.high?.url || item.snippet.thumbnails.medium?.url,
        }));
        setVideos(videoData);
      } else {
        setError('No videos found.');
      }
    } catch (err) {
      setError('Could not load videos. Please check API Key restrictions.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [API_KEY, CHANNEL_ID]);

  // Auto-load on mount
  useEffect(() => {
    fetchYouTubeVideos();

    const handleResize = () => {
      if (window.innerWidth < 768) setSlidesPerView(1);
      else if (window.innerWidth < 992) setSlidesPerView(2);
      else if (window.innerWidth < 1200) setSlidesPerView(3);
      else setSlidesPerView(4);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [fetchYouTubeVideos]);

  const handlePrev = () => setCurrentIndex(prev => Math.max(0, prev - 1));
  const handleNext = () => setCurrentIndex(prev => Math.min(videos.length - slidesPerView, prev + 1));

  if (loading) return <div style={{ color: 'white', textAlign: 'center', padding: '50px' }}>Loading Gallery...</div>;
  if (error) return <div style={{ color: 'red', textAlign: 'center', padding: '50px' }}>{error}</div>;

  return (
    <section className="gallery-two" id="youtube-gallery" style={{ padding: '60px 0', background: '#070410' }}>
      <div className="container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
          <h2 style={{ color: '#fff', margin: 0 }}>Latest Videos</h2>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={handlePrev} disabled={currentIndex === 0} style={navBtnStyle}>←</button>
            <button onClick={handleNext} disabled={currentIndex >= videos.length - slidesPerView} style={navBtnStyle}>→</button>
          </div>
        </div>

        <div style={{ overflow: 'hidden' }}>
          <div style={{
            display: 'flex',
            gap: '20px',
            transition: 'transform 0.5s ease',
            transform: `translateX(-${currentIndex * (100 / slidesPerView)}%)`
          }}>
            {videos.map((video) => (
              <div key={video.id} style={{ flexShrink: 0, width: `calc(${100 / slidesPerView}% - 15px)` }}>
                <div 
                  onClick={() => setSelectedVideo(video)}
                  style={{ cursor: 'pointer', background: '#110d1f', borderRadius: '12px', overflow: 'hidden' }}
                >
                  <img src={video.thumbnail} alt={video.title} style={{ width: '100%', aspectRatio: '16/9', objectFit: 'cover' }} />
                  <div style={{ padding: '15px' }}>
                    <h4 style={{ color: '#fff', fontSize: '14px', margin: 0, height: '50px', overflow: 'hidden' }}>{video.title}</h4>
                    <p style={{ color: '#6d239b', fontSize: '12px', margin: '0 0 5px' }}>{video.subtitle}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Video Lightbox */}
      {selectedVideo && (
        <div 
          style={modalOverlayStyle} 
          onClick={() => setSelectedVideo(null)}
        >
          <div style={{ width: '90%', maxWidth: '900px', position: 'relative' }} onClick={e => e.stopPropagation()}>
            <div style={{ paddingBottom: '56.25%', height: 0 }}>
              <iframe
                src={`https://www.youtube.com/embed/${selectedVideo.id}?autoplay=1`}
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', borderRadius: '12px' }}
              ></iframe>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

const navBtnStyle = {
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  border: '1px solid #332d41',
  background: 'none',
  color: 'white',
  cursor: 'pointer'
};

const modalOverlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.9)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000
};